import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { FaEdit, FaFileAlt, FaWpforms } from "react-icons/fa";
import PeopleIcon from "@mui/icons-material/People";
import Swal from "sweetalert2"; // Import SweetAlert for confirmation and alerts


import baseURL from "../../config";
import Navbar from "../../Navbar/Navbar";
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Button, Typography, CircularProgress, Box, Modal, TextField, TablePagination,
  Tooltip, IconButton, Stack, Grid
} from "@mui/material";
import { motion } from "framer-motion";
import { MdCheckCircle, MdCancel } from "react-icons/md";
import ToggleButton from "@mui/material/ToggleButton";

const CrewingAgentTable = () => {
  const [crewingAgents, setCrewingAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // 🔹 Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Default: 5 rows per page
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);


  const navigate = useNavigate();

  useEffect(() => {
    const fetchCrewingAgents = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`${baseURL}/api/getcrewingAgentDetails`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCrewingAgents(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error?.response?.data?.message);
        setLoading(false);
      }
    };

    fetchCrewingAgents();
  }, []);

  if (loading) {
    return <CircularProgress />;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
      .replace(/\//g, "-");
  };

  // Utility to get file view link


  const handleViewDocuments = async (agent) => {
    try {
      // ✅ Clear old documents immediately before opening modal
      setDocuments([]);

      // ✅ Open the modal
      setSelectedAgent(agent);
      setOpen(true);

      // ✅ Call the API to get documents for the agent
      const response = await axios.get(`${baseURL}/api/getDocumentsForAgent/${agent._id}`);

      // ✅ Set the documents data in state
      setDocuments(response.data);

    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);  // Clear documents in case of error
    }
  };



  const handleClose = () => {
    setOpen(false);
    setSelectedAgent(null);
  };

  const handleToggle = async (agentId, newStatus) => {


    // Confirm before deactivating
    if (!newStatus) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to deactivate this crewing agent?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, deactivate it!",
      });

      if (!result.isConfirmed) {
        return; // Stop if the user cancels
      }
    }
    const token = localStorage.getItem("token");

    try {
      const apiEndpoint = newStatus
        ? `${baseURL}/api/crewingAgent/approvedCrewing/${agentId}`
        : `${baseURL}/api/crewingAgent/unapprovedCrewing/${agentId}`;

      await axios.post(apiEndpoint, { approved: newStatus }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Show success alert
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `crewing agent has been ${newStatus ? "activated" : "deactivated"} successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });

      // Refresh the page to reflect the changes
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error("Error updating vessel status:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update crewing agent status. Please try again.",
      });
    }
  };

  const handleEdit = (agent) => {
    navigate("/Crewing_Agent", {
      state: {
        agent: {
          ...agent,
          contactPersonTitle: agent.contactPerson?.title || "",
          contactPersonName: agent.contactPerson?.name || "",
        },
      },
    });
  };

  const handleFormIClick = (crewingAgentId) => {
    navigate(`/CrewingAgentFormIList/${crewingAgentId}`);
  };


  // 🔹 Filter agents based on search query
  const filteredAgents = crewingAgents.filter((agent) =>
    agent.agentName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 🔹 Apply Pagination
  const paginatedAgents = filteredAgents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // 🔹 Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };
  return (
    <>
      <Navbar />
      {/* Error message */}
      {error && (
        <Box p={4} textAlign="center" bgcolor="#fdecea" color="#b71c1c">
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Box>
      )}

      <div className="CrewingTable-container" style={{ padding: "20px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            label="Search Agent"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mr: 2 }}
          />
          <Link to="/contractDocument">
            <Button variant="contained" sx={{ backgroundColor: "#C30B12" }}>
              Contract Documents
            </Button>
          </Link>

          <Typography variant="h5" gutterBottom sx={{ flexGrow: 1, textAlign: "center" }}>
            <PeopleIcon sx={{ marginRight: 1, color: "#C30B12" }} />
            Crewing Agent Table
          </Typography>

          <Link to="/Crewing_Agent">
            <Button variant="contained" sx={{ backgroundColor: "#C30B12" }}>
              Add Crewing Agent
            </Button>
          </Link>
        </Box>


        <TableContainer component={Paper} >
          <Table >
            <TableHead sx={{ backgroundColor: "#C30B12", height: "30px" }}>
              <TableRow sx={{ height: "30px" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: "60px" }}>Sr. No.</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: "180px" }}>Agent Details</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: "250px" }}>Contact Person</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: "250px" }}>Senior Manager</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: "450px" }}>Documents</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: "100px" }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedAgents.length > 0 ? (
                paginatedAgents.map((agent, index) => (
                  <TableRow key={agent._id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <strong>Name:</strong> {agent.agentName} <br />
                      <strong>Short Name:</strong> {agent.shortName} <br />
                      <strong>Email:</strong> {agent.email} <br />
                      <strong>Address:</strong> {agent.address}
                    </TableCell>

                    <TableCell>
                      <strong>Name:</strong>{`${agent.contactPerson.title} ${agent.contactPerson.name}`} <br />
                      <strong>Country:</strong> {agent.country} <br />
                      <strong>Number:</strong> {agent.contactNumber}
                    </TableCell>

                    <TableCell>
                      {Array.isArray(agent.seniorManager) && agent.seniorManager.length > 0 ? (
                        <>
                          <Typography variant="body2">
                            <strong>Name:</strong> {agent.seniorManager[0]?.name || ""}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Email:</strong> {agent.seniorManager[0]?.email || ""}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Number:</strong> {agent.seniorManager[0]?.number || ""}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2"></Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}> {/* reduced spacing */}

                        {/* File Section - Certificates in one row */}
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          {agent.ISOCertificate?.filePath && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight="bold">ISO:</Typography>
                              <Tooltip title="View ISO Certificate" arrow>
                                <IconButton
                                  href={`${baseURL}/${agent.ISOCertificate.filePath.replace(/\\/g, "/")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  sx={{ color: "#1976d2", p: 0.5 }}
                                >
                                  <FaFileAlt />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {agent.RPSLicense?.filePath && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight="bold">RPS:</Typography>
                              <Tooltip title="View RPS License" arrow>
                                <IconButton
                                  href={`${baseURL}/${agent.RPSLicense.filePath.replace(/\\/g, "/")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  sx={{ color: "#1976d2", p: 0.5 }}
                                >
                                  <FaFileAlt />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {agent.cirtificatteOfIncorporation?.filePath && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight="bold">COI:</Typography>
                              <Tooltip title="View Certificate of Incorporation" arrow>
                                <IconButton
                                  href={`${baseURL}/${agent.cirtificatteOfIncorporation.filePath.replace(/\\/g, "/")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  sx={{ color: "#1976d2", p: 0.5 }}
                                >
                                  <FaFileAlt />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {/* Dates Grid - compact version */}
                        <Grid container spacing={0.5} mt={0.5}>
                          {agent.ISOCertificateIssuedDate && (
                            <Grid item xs={6}>
                              <Typography variant="caption">
                                <strong>ISO Issued:</strong><br />
                                {formatDate(agent.ISOCertificateIssuedDate)}
                              </Typography>
                            </Grid>
                          )}
                          {agent.ISOCertificateExpiryDate && (
                            <Grid item xs={6}>
                              <Typography variant="caption">
                                <strong>ISO Expiry:</strong> <br />
                                {formatDate(agent.ISOCertificateExpiryDate)}
                              </Typography>
                            </Grid>
                          )}
                          {agent.RPSLicenseIssuedDate && (
                            <Grid item xs={6}>
                              <Typography variant="caption">
                                <strong>RPS Issued:</strong> <br />
                                {formatDate(agent.RPSLicenseIssuedDate)}
                              </Typography>
                            </Grid>
                          )}
                          {agent.RPSLicenseExpiryDate && (
                            <Grid item xs={6}>
                              <Typography variant="caption">
                                <strong>RPS Expiry:</strong><br />
                                {formatDate(agent.RPSLicenseExpiryDate)}
                              </Typography>
                            </Grid>
                          )}
                          {agent.Audit && (
                            <Grid item xs={6}>
                              <Typography variant="caption">
                                <strong>Audit Date:</strong> <br />
                                {formatDate(agent.Audit)}
                              </Typography>
                            </Grid>
                          )}
                          {agent.AuditStatus && (
                            <Grid item xs={6}>
                              <Typography variant="caption">
                                <strong>Status:</strong> <br />
                                {agent.AuditStatus}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Stack>
                    </TableCell>



                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <FaEdit
                          onClick={() => handleEdit(agent)}
                          style={{ cursor: "pointer", color: "blue", fontSize: "18px" }}
                        />

                        <FaFileAlt
                          onClick={() => handleViewDocuments(agent)}
                          style={{ cursor: "pointer", color: "green", fontSize: "18px" }}
                          title="View Documents / Contract"
                        />

                        {/* Toggle Active/Deactive */}
                        <motion.div
                          whileTap={{ scale: 0.9 }}
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ToggleButton
                            value="check"
                            selected={isActive}
                            onChange={() => handleToggle(agent._id, !agent.approved)}
                            sx={{
                              backgroundColor: agent.approved ? "green" : "red",
                              color: "white",
                              borderRadius: "20px",
                              fontWeight: "bold",
                              "&:hover": {
                                backgroundColor: agent.approved ? "#0b8a0b" : "#b30000",
                              },
                              transition: "background-color 0.3s ease-in-out",
                            }}
                          >
                            {agent.approved ? <MdCheckCircle /> : <MdCancel />}
                            {agent.approved ? " Actived" : " Deactived"}
                          </ToggleButton>
                        </motion.div>

                        {/* {Form I} */}
                        <Tooltip title="Form I List">
                          <span>
                            <FaWpforms
                              onClick={() => handleFormIClick(agent._id)}
                              style={{ cursor: "pointer", color: "#1976d2", fontSize: "18px" }}
                            />
                          </span>
                        </Tooltip>




                      </Box>
                    </TableCell>

                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="8" align="center">
                    No crewing agents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 🔹 Table Pagination Component */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]} // Options for rows per page
          component="div"
          count={filteredAgents.length} // Total number of items
          rowsPerPage={rowsPerPage} // Rows per page
          page={page} // Current page
          onPageChange={handleChangePage} // Handle page change
          onRowsPerPageChange={handleChangeRowsPerPage} // Handle rows per page change
        />

        {/* Modal */}
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "10px",
            }}
          >
            <Typography
              id="modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: "bold", marginBottom: "10px" }}
            >
              📄  Documents for{" "}
              <span style={{ color: "#1976d2" }}>
                {selectedAgent?.crewingAgentName}
              </span>
            </Typography>

            <Box
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                padding: "5px 10px",
                backgroundColor: "#f9f9f9",
                borderRadius: "5px",
              }}
            >
              {/* ✅ Add this condition to avoid errors */}
              {documents && documents.documentTypes && Object.keys(documents.documentTypes).length > 0 ? (
                Object.keys(documents.documentTypes).map((key) => {
                  const filePath = documents.documentTypes[key].path;
                  const finalURL = filePath.startsWith("http")
                    ? filePath
                    : `${baseURL}/${filePath.replace(/\\/g, "/")}`;

                  return (
                    <Box
                      key={key}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        borderBottom: "1px solid #e0e0e0",
                        transition: "background-color 0.3s",
                        "&:hover": {
                          backgroundColor: "#f3f3f3",
                        },
                      }}
                    >
                      <a
                        href={finalURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: "none",
                          color: "#1976d2",
                          fontWeight: "500",
                        }}
                      >
                        📁 {key} - {documents.documentTypes[key].filename}
                      </a>
                    </Box>
                  );
                })
              ) : (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: "#888",
                    fontStyle: "italic",
                  }}
                >
                  📂 No documents uploaded for this agent.
                </Typography>
              )}

            </Box>

            <Box
              sx={{
                textAlign: "right",
                marginTop: "20px",
              }}
            >
              <button
                onClick={handleClose}
                style={{
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: "5px",
                  fontSize: "14px",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                }}
              >
                Close
              </button>
            </Box>
          </Box>
        </Modal>



      </div>
    </>
  );
};

export default CrewingAgentTable;
